import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, User, Briefcase, GitBranch, Wallet, Calendar, Clock,
  FileText, CheckCircle, History, Upload, Download, Trash2, AlertTriangle,
  Phone, Mail, MapPin, Shield, Eye, EyeOff, X,
  HomeIcon
} from 'lucide-react';
import useEmployeeMasters from '../../hooks/useEmployeeMasters';
import { displayEmployeeType } from '../../utils/employeeTypeLabels';
import LifecycleActionModal from './LifecycleActionModal';
import EmployeeAvatar from '../common/EmployeeAvatar';
import EmployeeSubmissionPreview from './EmployeeSubmissionPreview';
import Employee360PFView from '../pf/Employee360PFView';
import Employee360IncomeTax from './Employee360IncomeTax';
import Employee360FestivalAdvance from './Employee360FestivalAdvance';
import Employee360PensionDocs from './Employee360PensionDocs';
import Employee360LeaveView from './Employee360LeaveView';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate as standardFormatDate } from '../../utils/dateUtils';

const TABS = [
  { id: 'personal', label: 'Personal', icon: User },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'service_book', label: 'Service Book', icon: History },
  { id: 'vpf', label: 'PF Subsystem', icon: Shield },
  { id: 'quarter', label: 'Quarter', icon: HomeIcon },
  { id: 'leave', label: 'Leave & Balances', icon: Calendar },
  { id: 'income_tax', label: 'Income Tax', icon: Wallet },
  { id: 'festival_advance', label: 'Festival Advance', icon: Briefcase },
  { id: 'pension_docs', label: 'Pension docs', icon: FileText }
];

const InfoGrid = ({ items }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {items.filter((i) => i.value != null && i.value !== '').map((i) => (
      <div key={i.label}>
        <p className="text-xs text-gray-500 uppercase tracking-wide">{i.label}</p>
        <p className="text-gray-900 font-medium">{String(i.value)}</p>
      </div>
    ))}
  </div>
);

const Employee360Profile = () => {
  const { hasPermission } = useAuth();
  const { employeeId } = useParams();

  const filteredTabs = TABS.filter(t => {
    if (t.id === 'personal') return true;
    if (t.id === 'documents') return hasPermission('emp:docs:official:view') || hasPermission('emp:docs:emp:view') || hasPermission('*');
    if (t.id === 'service_book') return hasPermission('emp:servicebook:view') || hasPermission('*');
    if (t.id === 'vpf') return hasPermission('emp:pfsubsys:view') || hasPermission('*');
    if (t.id === 'quarter') return hasPermission('emp:quarter:edit') || hasPermission('*');
    if (t.id === 'leave') return hasPermission('emp:leave:view') || hasPermission('*');
    if (t.id === 'income_tax') return hasPermission('emp:it:view') || hasPermission('*');
    if (t.id === 'festival_advance') return hasPermission('emp:fadv:create') || hasPermission('emp:fadv:approve') || hasPermission('emp:fadv:reject') || hasPermission('*');
    if (t.id === 'pension_docs') return hasPermission('emp:pensiondoc:view') || hasPermission('*');
    return false;
  });
  const navigate = useNavigate();
  const { masters, searchManagers } = useEmployeeMasters();
  const [profile, setProfile] = useState(null);
  const [photoRefreshKey, setPhotoRefreshKey] = useState(0);
  const [timeline, setTimeline] = useState([]);
  const [approval, setApproval] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'personal';

  const setTab = (tabId, replace = false) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!tabId || tabId === 'personal') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', tabId);
    }
    setSearchParams(nextParams, { replace });
  };
  const [loading, setLoading] = useState(true);
  const [lifecycleAction, setLifecycleAction] = useState(null);
  const [uploadType, setUploadType] = useState('');
  const [uploadExpiry, setUploadExpiry] = useState('');
  const [docChecklist, setDocChecklist] = useState(null);
  const [verifyRemarks, setVerifyRemarks] = useState('');
  const [showSensitive, setShowSensitive] = useState(false);
  const [vpfData, setVpfData] = useState(null);
  const [vpfLoading, setVpfLoading] = useState(false);
  const [vpfError, setVpfError] = useState('');
  const [vpfSuccess, setVpfSuccess] = useState('');
  const [vpfRejectReason, setVpfRejectReason] = useState('');
  const [vpfActionLoading, setVpfActionLoading] = useState(false);

  // Service book state
  const [serviceBook, setServiceBook] = useState([]);
  const [serviceBookLoading, setServiceBookLoading] = useState(false);

  // Document management states
  const [docRibbon, setDocRibbon] = useState('official'); // 'official' or 'employee'
  const [officialDocuments, setOfficialDocuments] = useState([]);
  const [employeeDocuments, setEmployeeDocuments] = useState([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showRemarksDialog, setShowRemarksDialog] = useState(false);
  const [selectedDocForRemarks, setSelectedDocForRemarks] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Quarter management states
  const [quarterValue, setQuarterValue] = useState(null);
  const [quarterSaving, setQuarterSaving] = useState(false);
  const [quarterError, setQuarterError] = useState('');
  const [quarterSuccess, setQuarterSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, t, a, d, checklist, official, employee] = await Promise.all([
        axios.get(`/v1/employee-master/${employeeId}/profile-360`),
        axios.get(`/v1/employee-master/${employeeId}/timeline`),
        axios.get(`/v1/employee-master/${employeeId}/approval-history`),
        axios.get(`/v1/employee-master/${employeeId}/documents`),
        axios.get(`/v1/employee-master/${employeeId}/documents/checklist`).catch(() => ({ data: null })),
        axios.get(`/v1/employee-master/${employeeId}/documents/official`).catch(() => ({ data: [] })),
        axios.get(`/v1/employees/${employeeId}/joining-form-submissions`).catch(() => ({ data: [] })),
      ]);
      setProfile(p.data);
      setPhotoRefreshKey((k) => k + 1);
      setTimeline(t.data);
      setApproval(a.data);
      setDocuments(d.data);
      setDocChecklist(checklist.data);
      setOfficialDocuments(official.data);
      setEmployeeDocuments(employee.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { load(); }, [load]);

  const loadVpf = useCallback(async () => {
    setVpfLoading(true);
    setVpfError('');
    try {
      const response = await axios.get(`/v1/vpf/employee/${employeeId}`);
      setVpfData(response.data);
    } catch (error) {
      if (error?.response?.status === 404) {
        setVpfData(null);
      } else {
        setVpfError(error?.response?.data?.detail || 'Failed to load VPF details.');
      }
    } finally {
      setVpfLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (tab === 'vpf') {
      loadVpf();
    }
  }, [tab, loadVpf]);

  const loadServiceBook = useCallback(async () => {
    setServiceBookLoading(true);
    try {
      const response = await axios.get(`/v1/employee-master/${employeeId}/service-book`);
      setServiceBook(response.data);
    } catch (error) {
      console.error("Failed to load service book:", error);
    } finally {
      setServiceBookLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (tab === 'service_book') {
      loadServiceBook();
    }
  }, [tab, loadServiceBook]);

  useEffect(() => {
    if (tab === 'quarter' && profile?.personal) {
      setQuarterValue(profile.personal.has_assigned_quarter);
    }
  }, [tab, profile]);

  const handleQuarterUpdate = async () => {
    setQuarterSaving(true);
    setQuarterError('');
    setQuarterSuccess('');
    try {
      await axios.put(`/v1/employee-master/${employeeId}`, {
        has_assigned_quarter: quarterValue,
      });
      setQuarterSuccess('Quarter status updated successfully.');
      load();
    } catch (error) {
      setQuarterError(error.response?.data?.detail || 'Failed to update quarter status.');
    } finally {
      setQuarterSaving(false);
    }
  };

  const maskSensitiveData = (data, type) => {
    if (!data) return 'Not provided';
    if (!showSensitive) {
      switch (type) {
        case 'aadhaar':
          return `****${data.slice(-4)}`;
        case 'pan':
          return `****${data.slice(-4)}`;
        default:
          return data;
      }
    }
    return data;
  };

  const formatDate = (date) => standardFormatDate(date, 'Not provided');

  const hasSectionData = (fields) => {
    return fields.some(field => field.value !== null && field.value !== undefined && field.value !== '');
  };

  const handleUpload = async (file) => {
    if (!file || !uploadType) return;
    const fd = new FormData();
    fd.append('file', file);
    await axios.post(
      `/v1/employee-master/${employeeId}/documents?document_type_id=${uploadType}&expiry_date=${uploadExpiry || ''}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    load();
  };

  const handleVerifyDoc = async (docId, status) => {
    await axios.post(`/v1/employee-master/${employeeId}/documents/${docId}/verify`, {
      status,
      remarks: verifyRemarks || undefined,
    });
    setVerifyRemarks('');
    load();
  };

  const handleReplaceDoc = async (docId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    await axios.post(`/v1/employee-master/${employeeId}/documents/${docId}/replace`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    load();
  };

  const downloadDoc = (submissionId, name) => {
    axios.get(`/v1/employees/${employeeId}/joining-form-submissions/${submissionId}/preview`, { responseType: 'blob' })
      .then((res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
      });
  };

  const downloadOfficialDoc = (documentId, name) => {
    axios.get(`/v1/employee-master/${employeeId}/documents/${documentId}/download`, { responseType: 'blob' })
      .then((res) => {
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.click();
      })
      .catch((err) => {
        console.error('Failed to download document:', err);
      });
  };

  const handleUploadOfficialDoc = async () => {
    if (!uploadDocType) {
      setUploadError('Document type is required');
      return;
    }
    if (!uploadFile) {
      setUploadError('File is required');
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      await axios.post(
        `/v1/employee-master/${employeeId}/documents?document_type_id=${uploadDocType}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setShowUploadDialog(false);
      setUploadDocType('');
      setUploadFile(null);
      setUploadError('');
      load();
    } catch (error) {
      setUploadError(error.response?.data?.detail || 'Failed to upload document. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleVerifyEmployeeDoc = async (submissionId, action) => {
    if (action === 'approved') {
      await axios.post(`/v1/employees/${employeeId}/joining-form-submissions/${submissionId}/approve`, {
        remarks: verifyRemarks || undefined,
      });
    } else {
      await axios.post(`/v1/employees/${employeeId}/joining-form-submissions/${submissionId}/reject`, {
        remarks: verifyRemarks,
      });
    }
    setVerifyRemarks('');
    setShowRemarksDialog(false);
    setSelectedDocForRemarks(null);
    load();
  };

  const openRemarksDialog = (doc) => {
    setSelectedDocForRemarks(doc);
    setShowRemarksDialog(true);
  };

  const handlePreviewDoc = async (doc) => {
    setPreviewDoc(doc);
    setShowPreviewDialog(true);
    setPreviewLoading(false); // No loading needed since we're rendering form_data directly
  };

  const handleClosePreview = () => {
    setShowPreviewDialog(false);
    if (previewDoc?.previewUrl) {
      URL.revokeObjectURL(previewDoc.previewUrl);
    }
    setPreviewDoc(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'SUBMITTED':
      default:
        return 'bg-amber-100 text-amber-800';
    }
  };

  const handleVpfApprove = async () => {
    const requestId = vpfData?.current_request?.id;
    if (!requestId) return;
    setVpfActionLoading(true);
    setVpfError('');
    setVpfSuccess('');
    try {
      await axios.post(`/v1/vpf/request/${requestId}/approve`);
      setVpfSuccess('VPF request approved successfully.');
      setVpfRejectReason('');
      await loadVpf();
    } catch (error) {
      setVpfError(error?.response?.data?.detail || 'Failed to approve VPF request.');
    } finally {
      setVpfActionLoading(false);
    }
  };

  const handleVpfReject = async () => {
    const requestId = vpfData?.current_request?.id;
    if (!requestId) return;
    if (!vpfRejectReason.trim()) {
      setVpfError('Rejection reason is required.');
      return;
    }
    setVpfActionLoading(true);
    setVpfError('');
    setVpfSuccess('');
    try {
      await axios.post(`/v1/vpf/request/${requestId}/reject`, {
        rejection_reason: vpfRejectReason.trim(),
      });
      setVpfSuccess('VPF request rejected.');
      setVpfRejectReason('');
      await loadVpf();
    } catch (error) {
      setVpfError(error?.response?.data?.detail || 'Failed to reject VPF request.');
    } finally {
      setVpfActionLoading(false);
    }
  };

  const handleVpfClose = async () => {
    const requestId = vpfData?.current_request?.id;
    if (!requestId) return;
    if (!window.confirm('Close this approved VPF request?')) return;
    setVpfActionLoading(true);
    setVpfError('');
    setVpfSuccess('');
    try {
      await axios.post(`/v1/vpf/request/${requestId}/close`, { confirm: true });
      setVpfSuccess('VPF request closed successfully.');
      await loadVpf();
    } catch (error) {
      setVpfError(error?.response?.data?.detail || 'Failed to close VPF request.');
    } finally {
      setVpfActionLoading(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading employee profile…</div>;
  }
  if (!profile?.personal) {
    return <div className="p-8 text-center text-red-600">Employee not found or access denied.</div>;
  }

  const p = profile.personal;
  const e = profile.employment;
  const h = profile.hierarchy;
  const pay = profile.payroll;

  const lifecycleActions = [
    // 'confirmation', 'promotion', 'transfer', 'salary_revision',
    // 'role_change', 'reporting_change', 'resignation', 'termination', 'exit_clearance',
  ];

  const renderVpfTab = () => {
    const request = vpfData?.current_request;
    const employeeDetails = vpfData?.employee || {};

    return (
      <div className="space-y-6">
        {vpfError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {vpfError}
          </div>
        )}
        {vpfSuccess && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
            {vpfSuccess}
          </div>
        )}

        {vpfLoading ? (
          <div className="rounded-lg border bg-white p-8 text-center text-gray-500">
            Loading VPF review...
          </div>
        ) : (
          <>
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="rounded-lg border border-gray-200 bg-white p-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Employee Details</h3>
                <InfoGrid
                  items={[
                    { label: 'Employee Name', value: p?.full_name },
                    { label: 'Employee Code', value: p?.emp_code },
                    { label: 'Designation', value: e?.designation },
                    { label: 'Department', value: e?.department },
                    { label: 'Pay Scale', value: e?.scale },
                    { label: 'Basic Salary', value: employeeDetails.basic_salary ? `₹${Number(employeeDetails.basic_salary).toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '-' },
                  ]}
                />
              </div>

              <div className="rounded-lg border border-gray-200 bg-white p-6">
                <h3 className="mb-4 text-lg font-semibold text-gray-900">Contribution Details</h3>
                {request ? (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Status</p>
                      <span className={`mt-1 inline-flex rounded-full px-3 py-1 text-xs font-medium ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-gray-500">Approval Status</p>
                      <p className="font-medium text-gray-900">{request.approval_status}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Contribution Type</p>
                      <p className="font-medium text-gray-900">
                        {request.contribution_type === 'FIXED_AMOUNT' ? 'Fixed Amount' : 'Percentage of Basic'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Amount</p>
                      <p className="font-medium text-gray-900">
                        {request.contribution_type === 'FIXED_AMOUNT'
                          ? `₹${Number(request.fixed_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                          : `${Number(request.percentage_of_basic || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}%`}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Effective From</p>
                      <p className="font-medium text-gray-900">{formatDate(request.effective_from_date)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Effective To</p>
                      <p className="font-medium text-gray-900">{formatDate(request.effective_to_date)}</p>
                    </div>
                    {request.rejection_reason && (
                      <div className="col-span-2">
                        <p className="text-gray-500">Rejection Reason</p>
                        <p className="mt-1 rounded-lg bg-red-50 p-3 text-red-800">{request.rejection_reason}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-500">
                    No VPF request is available for this employee.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">VPF Actions</h3>
                  <p className="text-sm text-gray-500">Approve, reject, or close the employee request here.</p>
                </div>
                {request && (
                  <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${request.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : request.status === 'REJECTED' ? 'bg-red-100 text-red-800' : request.status === 'CLOSED' ? 'bg-slate-100 text-slate-700' : 'bg-amber-100 text-amber-800'}`}>
                    {request.status} / {request.approval_status}
                  </span>
                )}
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div className="rounded-lg bg-gray-50 p-4">
                  <label className="block text-sm font-medium text-gray-700">Rejection Reason</label>
                  <textarea
                    value={vpfRejectReason}
                    onChange={(event) => setVpfRejectReason(event.target.value)}
                    rows={4}
                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                    placeholder="Required when rejecting"
                    disabled={!request || request.status !== 'PENDING' || request.approval_status !== 'PENDING_APPROVAL'}
                  />
                </div>

                <div className="flex flex-col justify-between gap-3">
                  <button
                    type="button"
                    onClick={handleVpfApprove}
                    disabled={!request || request.status !== 'PENDING' || request.approval_status !== 'PENDING_APPROVAL' || vpfActionLoading}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-3 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={handleVpfReject}
                    disabled={!request || request.status !== 'PENDING' || request.approval_status !== 'PENDING_APPROVAL' || vpfActionLoading || !vpfRejectReason.trim()}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <X className="h-4 w-4" />
                    Reject
                  </button>
                  <button
                    type="button"
                    onClick={handleVpfClose}
                    disabled={!request || request.status !== 'ACTIVE' || request.approval_status !== 'APPROVED' || vpfActionLoading}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Clock className="h-4 w-4" />
                    Close
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-6">
              <h3 className="text-lg font-semibold text-gray-900">History</h3>
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-700">
                    <tr>
                      <th className="px-4 py-3 text-left">Contribution</th>
                      <th className="px-4 py-3 text-left">Amount</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Approval</th>
                      <th className="px-4 py-3 text-left">Created</th>
                      <th className="px-4 py-3 text-left">Updated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(vpfData?.history || []).map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          {row.contribution_type === 'FIXED_AMOUNT' ? 'Fixed Amount' : 'Percentage of Basic'}
                        </td>
                        <td className="px-4 py-3">
                          {row.contribution_type === 'FIXED_AMOUNT'
                            ? `₹${Number(row.fixed_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
                            : `${Number(row.percentage_of_basic || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}%`}
                        </td>
                        <td className="px-4 py-3">{row.status}</td>
                        <td className="px-4 py-3">{row.approval_status}</td>
                        <td className="px-4 py-3">{formatDate(row.created_at)}</td>
                        <td className="px-4 py-3">{formatDate(row.updated_at)}</td>
                      </tr>
                    ))}
                    {(vpfData?.history || []).length === 0 && (
                      <tr>
                        <td colSpan="6" className="px-4 py-6 text-center text-gray-500">
                          No VPF history found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderQuarterTab = () => {
    return (
      <div className="space-y-6">
        {quarterError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {quarterError}
          </div>
        )}
        {quarterSuccess && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
            {quarterSuccess}
          </div>
        )}

        <div className="rounded-lg border border-gray-200 bg-white p-6">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 flex items-center">
            <HomeIcon className="w-5 h-5 mr-2 text-blue-600" />
            Quarter Details
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Need Government Quarter?
              </label>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="has_assigned_quarter"
                    value="true"
                    checked={quarterValue === true}
                    onChange={() => setQuarterValue(true)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="has_assigned_quarter"
                    value="false"
                    checked={quarterValue === false}
                    onChange={() => setQuarterValue(false)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700">No</span>
                </label>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Current Status:</span>{' '}
                  {profile?.personal?.has_assigned_quarter === true ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      No
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleQuarterUpdate}
                  disabled={quarterSaving || quarterValue === profile?.personal?.has_assigned_quarter}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {quarterSaving ? 'Saving...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <button type="button" onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900 mb-4">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back
      </button>

      <div className="bg-white rounded-xl shadow p-6 mb-6 flex flex-col md:flex-row gap-6">
        <EmployeeAvatar
          employeeId={employeeId}
          refreshKey={photoRefreshKey}
          size="xl"
          borderClass="border-2 border-blue-200"
          fallbackClass="bg-blue-100 text-blue-400"
        />
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{p.full_name}</h1>
          <p className="text-gray-600">{p.emp_code} · {e.designation} · {e.department}</p>
          <div className="flex flex-wrap gap-2 mt-2">
            {/* <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">{e.employment_status}</span>
            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{e.workflow_status}</span> */}
            <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">
              {e.employee_type_display || displayEmployeeType(e.employee_type)}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-start">
          {lifecycleActions.map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setLifecycleAction(a)}
              className="text-xs px-3 py-1.5 border rounded-md hover:bg-gray-50 capitalize"
            >
              {a.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mb-4 border-b bg-white rounded-t-lg px-2 pt-2">
        {filteredTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`flex items-center gap-1 px-3 py-2 text-sm rounded-t-md ${tab === id ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-b-xl shadow p-6">
        {tab === 'personal' && (
          <div className="space-y-6">
            {/* Sensitive Data Toggle */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setShowSensitive(!showSensitive)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                {showSensitive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showSensitive ? 'Hide sensitive data' : 'Show sensitive data'}
              </button>
            </div>

            {/* Section 1: Basic Information */}
            {hasSectionData([
              { value: p.emp_code },
              { value: p.first_name },
              { value: p.middle_name },
              { value: p.last_name },
              { value: p.date_of_birth },
              { value: p.gender },
              { value: p.marital_status },
              { value: p.blood_group },
              { value: p.father_name },
              { value: p.mother_name },
            ]) && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <User className="w-5 h-5 mr-2 text-blue-600" />
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {p.emp_code && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Employee Code</p>
                        <p className="text-gray-900 font-medium">{p.emp_code}</p>
                      </div>
                    )}
                    {p.first_name && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">First Name</p>
                        <p className="text-gray-900 font-medium">{p.first_name}</p>
                      </div>
                    )}
                    {p.middle_name && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Middle Name</p>
                        <p className="text-gray-900 font-medium">{p.middle_name}</p>
                      </div>
                    )}
                    {p.last_name && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Last Name</p>
                        <p className="text-gray-900 font-medium">{p.last_name}</p>
                      </div>
                    )}
                    {p.date_of_birth && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Date of Birth</p>
                        <p className="text-gray-900 font-medium">{formatDate(p.date_of_birth)}</p>
                      </div>
                    )}
                    {p.gender && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Gender</p>
                        <p className="text-gray-900 font-medium capitalize">{p.gender}</p>
                      </div>
                    )}
                    {p.marital_status && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Marital Status</p>
                        <p className="text-gray-900 font-medium capitalize">{p.marital_status}</p>
                      </div>
                    )}
                    {p.blood_group && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Blood Group</p>
                        <p className="text-gray-900 font-medium">{p.blood_group}</p>
                      </div>
                    )}
                    {p.father_name && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Father Name</p>
                        <p className="text-gray-900 font-medium">{p.father_name}</p>
                      </div>
                    )}
                    {p.mother_name && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Mother Name</p>
                        <p className="text-gray-900 font-medium">{p.mother_name}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

            {/* Section 2: Contact Information */}
            {hasSectionData([
              { value: p.official_email },
              { value: p.personal_email },
              { value: p.phone },
              { value: p.alternate_phone },
              { value: p.emergency_contact_name },
              { value: p.emergency_contact_number },
            ]) && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Phone className="w-5 h-5 mr-2 text-blue-600" />
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {p.official_email && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Official Email</p>
                        <p className="text-gray-900 font-medium">{p.official_email}</p>
                      </div>
                    )}
                    {p.personal_email && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Personal Email</p>
                        <p className="text-gray-900 font-medium">{p.personal_email}</p>
                      </div>
                    )}
                    {p.phone && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Mobile Number</p>
                        <p className="text-gray-900 font-medium">{p.phone}</p>
                      </div>
                    )}
                    {p.alternate_phone && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Alternate Mobile Number</p>
                        <p className="text-gray-900 font-medium">{p.alternate_phone}</p>
                      </div>
                    )}
                    {p.emergency_contact_name && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Emergency Contact Name</p>
                        <p className="text-gray-900 font-medium">{p.emergency_contact_name}</p>
                      </div>
                    )}
                    {p.emergency_contact_number && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Emergency Contact Number</p>
                        <p className="text-gray-900 font-medium">{p.emergency_contact_number}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

            {/* Section 3: Address */}
            {hasSectionData([
              { value: p.current_address },
              { value: p.permanent_address },
              { value: p.city },
              { value: p.state },
              { value: p.pin_code },
            ]) && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <MapPin className="w-5 h-5 mr-2 text-blue-600" />
                    Address
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {p.current_address && (
                      <div className="md:col-span-2">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Current Address</p>
                        <p className="text-gray-900 font-medium">{p.current_address}</p>
                      </div>
                    )}
                    {p.permanent_address && (
                      <div className="md:col-span-2">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Permanent Address</p>
                        <p className="text-gray-900 font-medium">{p.permanent_address}</p>
                      </div>
                    )}
                    {p.city && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">City</p>
                        <p className="text-gray-900 font-medium">{p.city}</p>
                      </div>
                    )}
                    {p.state && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">State</p>
                        <p className="text-gray-900 font-medium">{p.state}</p>
                      </div>
                    )}
                    {p.pin_code && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">PIN Code</p>
                        <p className="text-gray-900 font-medium">{p.pin_code}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

            {/* Section 4: Identity Information */}
            {hasSectionData([
              { value: p.pan_number },
              { value: p.aadhaar_number },
            ]) && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-blue-600" />
                    Identity Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {p.pan_number && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">PAN Number</p>
                        <p className="text-gray-900 font-medium">{maskSensitiveData(p.pan_number, 'pan')}</p>
                      </div>
                    )}
                    {p.aadhaar_number && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Aadhaar Number</p>
                        <p className="text-gray-900 font-medium">{maskSensitiveData(p.aadhaar_number, 'aadhaar')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

            {/* Section 5: Additional Information */}
            {p.physically_disabled !== undefined && p.physically_disabled !== null && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-blue-600" />
                  Additional Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Physically Disabled</p>
                    <p className="text-gray-900 font-medium">{p.physically_disabled ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Section 6: Service Details */}
            {hasSectionData([
              { value: e.employee_type_display || displayEmployeeType(e.employee_type) },
              { value: e.designation_type },
              { value: e.join_date },
              { value: e.retirement_date },
              { value: e.scale },
            ]) && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Briefcase className="w-5 h-5 mr-2 text-blue-600" />
                    Service Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(e.employee_type_display || displayEmployeeType(e.employee_type)) && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Employee Type</p>
                        <p className="text-gray-900 font-medium">{e.employee_type_display || displayEmployeeType(e.employee_type)}</p>
                      </div>
                    )}
                    {e.designation_type && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Designation Type</p>
                        <p className="text-gray-900 font-medium">{e.designation_type}</p>
                      </div>
                    )}
                    {e.scale && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Pay Scale</p>
                        <p className="text-gray-900 font-medium">{e.scale}</p>
                      </div>
                    )}
                    {e.join_date && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Join Date</p>
                        <p className="text-gray-900 font-medium">{formatDate(e.join_date)}</p>
                      </div>
                    )}
                    {e.retirement_date && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Retirement Date (DOR)</p>
                        <p className="text-gray-900 font-medium">{formatDate(e.retirement_date)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
          </div>
        )}
        {tab === 'employment' && (
          <InfoGrid items={[
            { label: 'Department', value: e.department },
            { label: 'Designation', value: e.designation },
            { label: 'Role', value: e.role },
            { label: 'Grade', value: e.grade },
            { label: 'Join Date', value: e.join_date },
            { label: 'Confirmation', value: e.confirmation_date },
            { label: 'Branch', value: e.hospital_branch },
            { label: 'Location', value: e.work_location },
          ]} />
        )}
        {tab === 'hierarchy' && (
          <InfoGrid items={[
            { label: 'Level', value: h.hierarchy_level },
            { label: 'Reporting Manager', value: h.reporting_manager },
            { label: 'Functional Manager', value: h.functional_manager },
            { label: 'Department Head', value: h.department_head },
            { label: 'Supervisor', value: h.supervisor },
          ]} />
        )}
        {tab === 'payroll' && (
          <div className="space-y-6">
            <InfoGrid items={[
              { label: 'Basic Salary', value: pay.basic_salary },
              { label: 'Grade Pay', value: pay.grade_pay },
              { label: 'Special Allowance', value: pay.special_allowance },
              { label: 'Bank', value: pay.bank_name },
              { label: 'Account', value: pay.bank_account_number },
              { label: 'IFSC', value: pay.bank_ifsc_code },
              { label: 'UPI', value: pay.upi_id },
            ]} />

            {/* Loans and Advances Section */}
            {profile.loans_advances && (
              <div className="mt-6 border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Loans & Advances</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* PF Adv */}
                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <h4 className="font-medium text-gray-900 mb-3">PF Adv</h4>
                    {profile.loans_advances.pf_loan.has_active ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Remaining Balance:</span>
                          <span className="font-medium">₹{Number(profile.loans_advances.pf_loan.remaining_balance).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Remaining Installments:</span>
                          <span className="font-medium">{profile.loans_advances.pf_loan.remaining_installments}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Loan Number:</span>
                          <span className="font-medium">{profile.loans_advances.pf_loan.loan_number || '—'}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No active PF adv</p>
                    )}
                  </div>

                  {/* Festival Advance */}
                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Festival Advance</h4>
                    {profile.loans_advances.festival_advance.has_active ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Remaining Balance:</span>
                          <span className="font-medium">₹{Number(profile.loans_advances.festival_advance.remaining_balance).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Remaining Installments:</span>
                          <span className="font-medium">{profile.loans_advances.festival_advance.remaining_installments}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Application Number:</span>
                          <span className="font-medium">{profile.loans_advances.festival_advance.application_number || '—'}</span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No active festival advance</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'attendance' && (
          <InfoGrid items={[
            { label: 'Month', value: profile.attendance?.month },
            { label: 'Present', value: profile.attendance?.present },
            { label: 'Absent', value: profile.attendance?.absent },
            { label: 'Total Records', value: profile.attendance?.total_days },
          ]} />
        )}
        {tab === 'shift' && (
          <>
            <p className="mb-2"><strong>Default Shift:</strong> {profile.shift_roster?.default_shift || '—'}</p>
            <ul className="text-sm space-y-1">
              {(profile.shift_roster?.upcoming || []).map((s) => (
                <li key={s.date} className="flex justify-between border-b py-1"><span>{s.date}</span><span>{s.shift}</span></li>
              ))}
            </ul>
          </>
        )}
        {tab === 'documents' && (
          <div className="space-y-6">
            {/* Document Checklist Summary */}
            {docChecklist && (
              <div className={`p-4 rounded-lg border ${docChecklist.complete ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <p className="text-sm font-medium">
                  Mandatory documents: {docChecklist.items?.filter((i) => i.is_mandatory && i.uploaded).length || 0}/{docChecklist.mandatory_count || 0}
                </p>
                <p className="text-xs text-gray-600">
                  Total registration documents: {docChecklist.total_count || 0}
                </p>
                <ul className="mt-2 text-sm space-y-1">
                  {(docChecklist.items || []).map((item) => (
                    <li key={item.document_type_id} className="flex items-center gap-2">
                      {item.uploaded ? <CheckCircle className="w-4 h-4 text-green-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                      {item.name}
                      {item.is_mandatory && <span className="text-red-600">*</span>}
                      {/* {item.verification_status && (
                        <span className="text-xs text-gray-500">({item.verification_status})</span>
                      )} */}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Horizontal Document Ribbon */}
            <div className="flex gap-2 border-b pb-2">
              <button
                type="button"
                onClick={() => setDocRibbon('official')}
                className={`px-4 py-2 rounded-t-lg text-sm font-medium ${docRibbon === 'official'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                Official Documents ({officialDocuments.length})
              </button>
              <button
                type="button"
                onClick={() => setDocRibbon('employee')}
                className={`px-4 py-2 rounded-t-lg text-sm font-medium ${docRibbon === 'employee'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                Employee Submitted Documents ({employeeDocuments.length})
              </button>
            </div>

            {/* Official Documents Section */}
            {docRibbon === 'official' && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-blue-600" />
                      Official Documents
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Documents uploaded and maintained by HR/Admin
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowUploadDialog(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Official Document
                  </button>
                </div>

                {/* Official Documents Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Document Type</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Document Name</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Uploaded On</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {officialDocuments.length > 0 ? (
                        officialDocuments.map((d) => {
                          const expiring = d.expiry_date && new Date(d.expiry_date) < new Date(Date.now() + 30 * 86400000);
                          return (
                            <tr key={d.id} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <div className="font-medium text-gray-900">{d.type_name}</div>
                                {d.expiry_date && (
                                  <div className={`text-xs flex items-center gap-1 mt-1 ${expiring ? 'text-red-600' : 'text-gray-500'}`}>
                                    {expiring && <AlertTriangle className="w-3 h-3" />}
                                    Expires: {formatDate(d.expiry_date)}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-gray-600">{d.file_name}</td>
                              <td className="px-4 py-3 text-gray-600">{formatDate(d.uploaded_at)}</td>
                              <td className="px-4 py-3">
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => downloadOfficialDoc(d.id, d.file_name)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                    title="Download"
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                  <label
                                    className="p-2 text-amber-600 hover:bg-amber-50 rounded cursor-pointer"
                                    title="Update"
                                  >
                                    <Upload className="w-4 h-4" />
                                    <input
                                      type="file"
                                      className="hidden"
                                      onChange={(ev) => handleReplaceDoc(d.id, ev.target.files?.[0])}
                                    />
                                  </label>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                            No official documents uploaded yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Employee Submitted Documents Section */}
            {docRibbon === 'employee' && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-blue-600" />
                    Employee Submitted Documents
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Documents uploaded by employees after joining
                  </p>
                </div>

                {/* Admin Managed Joining Forms Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Document Type</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Status</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { code: 'PF_NOM', title: 'PF Nomination' },
                        { code: 'FAMILY', title: 'Family Details' },
                        { code: 'MARR_DECL', title: 'Marriage Declaration' },
                        { code: 'REL_DECL', title: 'Relationship Declaration' }
                      ].map((formDef) => {
                        const d = employeeDocuments.find(sub => sub.form_code === formDef.code);
                        const status = d ? d.status : 'NOT CREATED';
                        
                        return (
                          <tr key={formDef.code} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">{formDef.title}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                                {status}
                              </span>
                              {d?.submitted_at && <div className="text-xs text-gray-500 mt-1">Submitted: {formatDate(d.submitted_at)}</div>}
                              {d?.approved_at && <div className="text-xs text-gray-500 mt-1">Approved: {formatDate(d.approved_at)}</div>}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                {!d && (
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/admin/employees/${employeeId}/forms/fill/${formDef.code}`)}
                                    className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 text-xs font-medium"
                                  >
                                    Start
                                  </button>
                                )}
                                {d && (d.status === 'SUBMITTED' || d.status === 'DRAFT' || d.status === 'REJECTED') && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/admin/employees/${employeeId}/forms/fill/${formDef.code}`)}
                                      className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 text-xs font-medium"
                                    >
                                      Continue
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                          if (window.confirm('Are you sure you want to approve this document? It will become read-only.')) {
                                              handleVerifyEmployeeDoc(d.id, 'approved');
                                          }
                                      }}
                                      className="px-3 py-1.5 bg-green-100 text-green-800 rounded hover:bg-green-200 text-xs font-medium"
                                    >
                                      Approve
                                    </button>
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        if (window.confirm('Are you sure you want to delete this document? You will need to start again.')) {
                                          try {
                                            await axios.delete(`/v1/employees/${employeeId}/joining-form-submissions/${d.id}`);
                                            load();
                                          } catch (error) {
                                            alert(error.response?.data?.detail || 'Failed to delete document');
                                          }
                                        }
                                      }}
                                      className="px-3 py-1.5 bg-red-100 text-red-800 rounded hover:bg-red-200 text-xs font-medium"
                                    >
                                      Delete
                                    </button>
                                  </>
                                )}
                                {d && d.status === 'APPROVED' && (
                                  <button
                                    type="button"
                                    onClick={() => handlePreviewDoc(d)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                    title="View Form"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
        {tab === 'service_book' && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <History className="w-5 h-5 mr-2 text-blue-600" />
              Service Book & Career History
            </h3>

            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sr. No.</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Effective Date</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Basic Pay</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {serviceBookLoading ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center text-sm text-gray-500">
                          Loading service history...
                        </td>
                      </tr>
                    ) : serviceBook.length > 0 ? (
                      serviceBook.map((row) => (
                        <tr key={row.sr_no} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.sr_no}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {row.effective_date ? standardFormatDate(row.effective_date, '-') : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.title === 'Joining' ? 'bg-blue-100 text-blue-800' :
                                row.title === 'Increment' ? 'bg-green-100 text-green-800' :
                                  row.title === 'Promotion' ? 'bg-purple-100 text-purple-800' :
                                    row.title === 'Historical Promotion' ? 'bg-violet-100 text-violet-800 border border-violet-200' :
                                      row.title === 'Functional Promotion' ? 'bg-indigo-100 text-indigo-800' :
                                      row.title === 'Transfer' ? 'bg-amber-100 text-amber-800' :
                                        row.title === 'Confirmation' ? 'bg-teal-100 text-teal-800' :
                                          row.title.includes('Suspension') ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                              }`}>
                              {row.title}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{row.designation || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.department || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                            {row.basic_pay > 0 ? `₹${row.basic_pay.toLocaleString()}` : '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={row.remarks || ''}>
                            {row.remarks || '-'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center text-sm text-gray-500">
                          No service history available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        {tab === 'vpf' && <Employee360PFView employeeId={employeeId} renderVpfTab={renderVpfTab} />}
        {tab === 'pf' && <Employee360PFView employeeId={employeeId} renderVpfTab={renderVpfTab} />}
        {tab === 'quarter' && renderQuarterTab()}
        {tab === 'income_tax' && <Employee360IncomeTax employeeId={employeeId} />}
        {tab === 'festival_advance' && <Employee360FestivalAdvance employeeId={employeeId} />}
        {tab === 'pension_docs' && <Employee360PensionDocs employeeId={employeeId} />}
        {tab === 'leave' && (
          <div className="space-y-6">
            {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-blue-900 text-lg">Leave Balances & Pension Leave Sheet</h3>
                <p className="text-xs text-blue-700">Detailed period entitlements, transactions ledger, and pension report</p>
              </div>
              <button
                onClick={() => window.open(`/api/v1/leave-sheet/employee/${employeeId}/excel`, '_blank')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Export Leave Sheet (Excel)</span>
              </button>
            </div> */}
            <Employee360LeaveView employeeId={employeeId} />
          </div>
        )}
        {tab === 'approval' && approval && (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
              {(approval.flow || []).map((step, i) => (
                <React.Fragment key={step}>
                  <span className="px-2 py-1 bg-gray-100 rounded">{step}</span>
                  {i < approval.flow.length - 1 && <span>→</span>}
                </React.Fragment>
              ))}
            </div>
            {(approval.records || []).map((r, i) => (
              <div key={i} className="border rounded p-4 text-sm">
                <p><strong>Status:</strong> {r.workflow_status}</p>
                {r.created_by && <p>Created by {r.created_by} at {r.created_at}</p>}
                {r.verified_by && <p>Verified by {r.verified_by} — {r.verifier_remarks}</p>}
                {r.approved_by && <p>Approved by {r.approved_by} — {r.approver_remarks}</p>}
              </div>
            ))}
          </div>
        )}
        {tab === 'timeline' && (
          <ol className="relative border-l border-gray-200 ml-3">
            {timeline.map((item, i) => (
              <li key={i} className="mb-6 ml-6">
                <span className="absolute -left-1.5 flex h-3 w-3 rounded-full bg-blue-500" />
                <p className="font-medium text-gray-900">{item.title}</p>
                <p className="text-xs text-gray-500">{item.date} {item.by && `· ${item.by}`}</p>
                {item.remarks && <p className="text-sm text-gray-600 mt-1">{item.remarks}</p>}
              </li>
            ))}
          </ol>
        )}
      </div>

      <LifecycleActionModal
        isOpen={!!lifecycleAction}
        action={lifecycleAction}
        employeeId={employeeId}
        masters={masters}
        searchManagers={searchManagers}
        onClose={() => setLifecycleAction(null)}
        onSuccess={load}
      />

      {/* Upload Official Document Dialog */}
      {showUploadDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload Official Document</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Type *</label>
                <select
                  value={uploadDocType}
                  onChange={(ev) => {
                    setUploadDocType(ev.target.value);
                    setUploadError('');
                  }}
                  className="w-full border rounded px-3 py-2 text-sm"
                >
                  <option value="">Select document type</option>
                  {(masters?.documentTypes || [])
                    .filter((t) => t.source === 'official' || !t.source)
                    .map((t) => (
                      <option key={t.value || t.id} value={t.value || t.id}>
                        {t.label || t.name}
                      </option>
                    ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File *</label>
                <input
                  type="file"
                  onChange={(ev) => {
                    setUploadFile(ev.target.files?.[0] || null);
                    setUploadError('');
                  }}
                  className="w-full border rounded px-3 py-2 text-sm"
                />
                {uploadFile && (
                  <p className="text-xs text-gray-500 mt-1">Selected: {uploadFile.name}</p>
                )}
              </div>
              {uploadError && (
                <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                  {uploadError}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowUploadDialog(false);
                  setUploadDocType('');
                  setUploadFile(null);
                  setUploadError('');
                }}
                className="px-4 py-2 border rounded text-sm hover:bg-gray-50"
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUploadOfficialDoc}
                disabled={isUploading}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remarks Dialog for Rejection */}
      {showRemarksDialog && selectedDocForRemarks && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {selectedDocForRemarks.verification_status === 'rejected' ? 'View Remarks' : 'Reject Document'}
            </h3>
            {selectedDocForRemarks.verification_status === 'rejected' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document</label>
                  <p className="text-sm text-gray-900">{selectedDocForRemarks.type_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
                  <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                    {selectedDocForRemarks.verification_remarks || 'No remarks provided'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rejected On</label>
                  <p className="text-sm text-gray-900">
                    {selectedDocForRemarks.verified_at ? formatDate(selectedDocForRemarks.verified_at) : 'N/A'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document</label>
                  <p className="text-sm text-gray-900">{selectedDocForRemarks.type_name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Remarks (required)</label>
                  <textarea
                    value={verifyRemarks}
                    onChange={(ev) => setVerifyRemarks(ev.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                    rows={3}
                    placeholder="Enter reason for rejection"
                  />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowRemarksDialog(false);
                  setSelectedDocForRemarks(null);
                  setVerifyRemarks('');
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm"
              >
                Cancel
              </button>
              {selectedDocForRemarks.verification_status !== 'rejected' && (
                <button
                  type="button"
                  onClick={() => handleVerifyEmployeeDoc(selectedDocForRemarks.id, 'rejected')}
                  disabled={!verifyRemarks.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm disabled:opacity-50"
                >
                  Reject
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview Dialog */}
      {showPreviewDialog && previewDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                Form Preview: {previewDoc.form_code}
              </h3>
              <button
                type="button"
                onClick={handleClosePreview}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              <EmployeeSubmissionPreview submission={previewDoc} readOnly={true} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Employee360Profile;
